import { useCallback, useEffect, useMemo, useRef } from 'react';

import { createSlice } from '@reduxjs/toolkit';
import { useRouter } from 'next/router';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';

import { PayloadAction } from '@techstyle/redux-core';

import { useLDFlags } from '../utils/LD/useLDFlags';

type SetSizesFromBraCalculatorPayload = {
  sizes: { bra: string; bralette: string };
  isBra: boolean;
  currentSelection: Record<string, any>;
};

export const braSizeSelections = createSlice({
  name: 'braSizeSelections',
  initialState: {
    selections: null,
    sizes: { bra: null, bralette: null },
    isBra: true,
  },
  reducers: {
    setSelectionState: (state, action) => {
      state.selections = action.payload;
    },
    setSizesFromBraCalculator: (
      state,
      action: PayloadAction<SetSizesFromBraCalculatorPayload>
    ) => {
      const { sizes, isBra, currentSelection } = action.payload;
      state.selections.size = currentSelection;
      state.isBra = isBra;
      state.sizes = sizes;
    },
  },
});

export const { setSelectionState, setSizesFromBraCalculator } =
  braSizeSelections.actions;

export const useCalculatedBraSizesToQuerySelector = () => {
  const { selections, sizes, isBra } = useSelector(
    (state: RootStateOrAny) => state.braSizeSelections
  );
  return {
    sizeBra: isBra ? selections?.size?.value : sizes.bra,
    sizeBralette: isBra ? sizes.bralette : selections?.size?.value,
  };
};

export function useBraSizeSelectionsInit(initialValue) {
  const dispatch = useDispatch();
  const selections = useSelector(
    (state: RootStateOrAny) => state.braSizeSelections.selections
  );
  const initRef = useRef(false);

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
      if (selections === null) {
        dispatch(setSelectionState(initialValue));
      }
    }
  }, [dispatch, initialValue, selections]);

  const setSelections = useCallback(
    newValueOrFunction => {
      const newValue =
        typeof newValueOrFunction === 'function'
          ? newValueOrFunction(selections)
          : newValueOrFunction;

      dispatch(setSelectionState(newValue));
    },
    [dispatch, selections]
  );

  return [selections || initialValue, setSelections];
}

export const useBraSizeNavigation = () => {
  const router = useRouter();
  const { 'show-bra-calculator-page-and-pdp-modal': showBraCalculator } =
    useLDFlags();
  const { sizeBra, sizeBralette } = useCalculatedBraSizesToQuerySelector();

  const isProcessing = useRef(false);

  const isSafari = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const ua = window.navigator.userAgent;
    return /Safari/.test(ua) && !/Chrome/.test(ua);
  }, []);

  const sizeParams = useMemo(
    () => ({
      sizeBra,
      sizeBralette,
    }),
    [sizeBra, sizeBralette]
  );

  const sizeParamsRef = useRef(sizeParams);
  useEffect(() => {
    sizeParamsRef.current = sizeParams;
  }, [sizeParams]);

  const needsQueryUpdate = useCallback(() => {
    if (!showBraCalculator) {
      return false;
    }
    if (typeof window === 'undefined') {
      return false;
    }

    const { sizeBra, sizeBralette } = sizeParamsRef.current;
    if (!sizeBra && !sizeBralette) {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    const currentBraSize = params.get('size_bra');
    const currentBraletteSize = params.get('size_bralette');

    return (
      (sizeBra && currentBraSize !== sizeBra) ||
      (sizeBralette && currentBraletteSize !== sizeBralette)
    );
  }, [showBraCalculator]);

  const createUpdatedQuery = useCallback(() => {
    if (!showBraCalculator) {
      return null;
    }

    const { sizeBra, sizeBralette } = sizeParamsRef.current;
    if (!sizeBra && !sizeBralette) {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    const query: Record<string, any> = {};

    params.forEach((value, key) => {
      query[key] = value;
    });

    if (sizeBra) {
      query.size_bra = sizeBra;
    }
    if (sizeBralette) {
      query.size_bralette = sizeBralette;
    }

    return query;
  }, [showBraCalculator]);

  // Direct URL update for Safari
  const updateUrlDirectly = useCallback(() => {
    if (typeof window === 'undefined' || isProcessing.current) {
      return;
    }
    if (!window.location.pathname.includes('/products/bras')) {
      return;
    }

    try {
      isProcessing.current = true;

      if (!needsQueryUpdate()) {
        isProcessing.current = false;
        return;
      }

      const { sizeBra, sizeBralette } = sizeParamsRef.current;

      const url = new URL(window.location.href);

      if (sizeBra) {
        url.searchParams.set('size_bra', sizeBra);
      }
      if (sizeBralette) {
        url.searchParams.set('size_bralette', sizeBralette);
      }

      window.history.replaceState({}, '', url.toString());

      router.replace(
        {
          pathname: window.location.pathname,
          query: Object.fromEntries(url.searchParams.entries()),
        },
        undefined,
        { shallow: true }
      );
    } catch (e) {
      console.error('Safari URL update error:', e);
    } finally {
      setTimeout(() => {
        isProcessing.current = false;
      }, 100);
    }
  }, [router, needsQueryUpdate]);

  // Standard router update for non-Safari browsers
  const updateWithRouter = useCallback(() => {
    if (typeof window === 'undefined' || isProcessing.current) {
      return;
    }
    if (!window.location.pathname.includes('/products/bras')) {
      return;
    }

    try {
      isProcessing.current = true;

      if (!needsQueryUpdate()) {
        isProcessing.current = false;
        return;
      }

      const updatedQuery = createUpdatedQuery();
      if (!updatedQuery) {
        isProcessing.current = false;
        return;
      }

      router
        .replace(
          { pathname: window.location.pathname, query: updatedQuery },
          undefined,
          { shallow: true, scroll: false }
        )
        .finally(() => {
          setTimeout(() => {
            isProcessing.current = false;
          }, 100);
        });
    } catch (e) {
      console.error('Router update error:', e);
      isProcessing.current = false;
    }
  }, [router, needsQueryUpdate, createUpdatedQuery]);

  const handleNavigation = useCallback(() => {
    if (isSafari) {
      updateUrlDirectly();
    } else {
      updateWithRouter();
    }
  }, [isSafari, updateUrlDirectly, updateWithRouter]);

  useEffect(() => {
    if (!showBraCalculator) {
      return;
    }

    // Specialized Safari event for back/forward navigation
    const handlePageShow = e => {
      if (e.persisted) {
        // This is triggered when page is restored from bfcache (back/forward)
        setTimeout(handleNavigation, 50);
      }
    };

    // Standard popstate event (back/forward button)
    const handlePopState = () => {
      setTimeout(handleNavigation, 50);
    };

    // This is critical for Safari - both capturing the load and hashchange events
    const handleLoad = () => {
      setTimeout(handleNavigation, 50);
    };

    // Setup event listeners
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('load', handleLoad);
    window.addEventListener('hashchange', handleLoad);

    // Handle initial URL
    setTimeout(handleNavigation, 100);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('hashchange', handleLoad);
    };
  }, [handleNavigation, showBraCalculator]);

  return null;
};
